<template>
  <div class="space-y-2">
    <LabelComponent>{{ label }}</LabelComponent>
    <Input
      v-model="input"
      type="text"
      required
      :id="id"
      :placeholder="placeholder"
      :data-testid="testId || 'text-input'"
      :disabled="disabled"
      @input="onInput"
    />
    <template v-if="error !== ''">
      <p class="text-sm font-medium text-error-500">
        {{ error }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { Input } from '../ui/input';
import LabelComponent from '../ui/label/LabelComponent.vue';

const props = defineProps<{
  id: string;
  label: string;
  disabled?: boolean;
  placeholder?: string;
  regex?: RegExp;
  testId?: string;
  enableError?: boolean;
}>();

const input = defineModel<string>('input');
const isValid = ref(true);
const emit = defineEmits(['update:isInputValid', 'error']);
const error = ref('');

// Watch for programmatic changes to input value
watch(
  () => input.value,
  (newValue) => {
    if (newValue && String(newValue).trim() !== '') {
      error.value = '';
      isValid.value = true;
      emit('update:isInputValid', true);
      emit('error', '');
    }
  },
);

const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.value;

  if (props.regex) {
    isValid.value = props.regex.test(value);
    if (!isValid.value) {
      emit('error', 'Invalid format');
      error.value = 'Invalid format';
    }
  } else {
    isValid.value = value.trim() !== '';
    if (!isValid.value) {
      emit('error', 'Input cannot be empty');
      error.value = 'Input cannot be empty';
    }
  }
  emit('update:isInputValid', isValid.value);

  if (isValid.value) {
    error.value = '';
    emit('error', '');
  }
};

watch(isValid, (newValue) => {
  emit('update:isInputValid', newValue);
  if (newValue) {
    error.value = '';
    emit('error', '');
  }
});
</script>
